import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { useRegions } from '@/features/admin/api'
import { useDistricts, useProvinces } from '@/features/hr/personel/api/referenceApi'
import { useProductSectors } from '@/features/projects/catalogApi'
import { isApiError } from '@/lib/api/errors'
import { useSaveCustomer } from '../api'
import { isValidUrl } from '../links'
import { DELIVERY, STAGES, type Customer } from '../types'
import { toastError, useCrm } from '../ui'

/** Listede olmayan eski kayıt değeri (serbest metin döneminden) seçenek olarak korunur. */
function withLegacy(names: string[], legacy?: string) {
  const options = names.map((name) => ({ value: name, label: name }))
  if (legacy && !names.some((n) => n.toLocaleLowerCase('tr') === legacy.toLocaleLowerCase('tr'))) options.unshift({ value: legacy, label: legacy })
  return options
}

export function CustomerFormModal({ customer, onDone }: { customer: Customer | null; onDone: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const save = useSaveCustomer()
  const { staff, projects } = useCrm()
  const schema = z.object({
    name: z.string().trim().min(2, t('crm.form.nameRequired')).max(120),
    legalName: z.string().trim().max(160),
    sector: z.string().trim().min(1, t('crm.form.sectorRequired')),
    projectId: z.string().min(1, t('crm.form.projectRequired')),
    stage: z.enum(STAGES),
    delivery: z.enum(DELIVERY),
    contactName: z.string().trim().min(2, t('crm.form.contactRequired')).max(80),
    contactTitle: z.string().trim().max(60),
    phone: z.string().trim().max(20),
    email: z.union([z.literal(''), z.email(t('crm.form.emailInvalid'))]),
    city: z.string().trim().max(40),
    district: z.string().trim().max(60),
    region: z.string().trim().max(40),
    taxNo: z.string().trim().regex(/^(\d{10}|\d{11})?$/, t('crm.form.taxNoInvalid')),
    taxOffice: z.string().trim().max(60),
    website: z.string().trim().max(120).refine((v) => !v || isValidUrl(v), t('crm.form.urlInvalid')),
    appUrl: z.string().trim().max(160).refine((v) => !v || isValidUrl(v), t('crm.form.urlInvalid')),
    adminUrl: z.string().trim().max(160).refine((v) => !v || isValidUrl(v), t('crm.form.urlInvalid')),
    address: z.string().trim().max(240),
    ownerId: z.string().min(1, t('crm.form.ownerRequired')),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: customer?.name ?? '',
      legalName: customer?.legalName ?? '',
      sector: customer?.sectorId ?? '',
      projectId: customer?.projectId ?? projects[0]?.id ?? '',
      stage: customer?.stage ?? 'lead',
      delivery: customer?.delivery ?? 'none',
      contactName: customer?.contactName ?? '',
      contactTitle: customer?.contactTitle ?? '',
      phone: customer?.phone ?? '',
      email: customer?.email ?? '',
      city: customer?.city ?? '',
      district: customer?.district ?? '',
      region: customer?.region ?? '',
      taxNo: customer?.taxNo ?? '',
      taxOffice: customer?.taxOffice ?? '',
      website: customer?.website ?? '',
      appUrl: customer?.appUrl ?? '',
      adminUrl: customer?.adminUrl ?? '',
      address: customer?.address ?? '',
      ownerId: customer?.ownerId ?? staff[0]?.id ?? '',
    },
  })
  const selectedProjectId = useWatch({ control: form.control, name: 'projectId' })
  const sectors = useProductSectors(selectedProjectId).data ?? []
  const previousProjectId = useRef(selectedProjectId)
  useEffect(() => {
    if (previousProjectId.current !== selectedProjectId) {
      form.setValue('sector', '')
      previousProjectId.current = selectedProjectId
    }
  }, [form, selectedProjectId])
  // İl/ilçe ortak lokasyon listesinden seçilir (kayıtta ad saklanır); bölge, veri kapsamı yetkilerinin
  // dayandığı tanımlı bölgelerden seçilir — serbest metin eşleşmeyince bölge sessizce boş kalıyordu.
  const provincesQ = useProvinces()
  const provinces = provincesQ.data ?? []
  const city = useWatch({ control: form.control, name: 'city' })
  const cityId = provinces.find((p) => p.name.toLocaleLowerCase('tr') === city.toLocaleLowerCase('tr'))?.id ?? ''
  const districtsQ = useDistricts(cityId)
  const regions = useRegions().data ?? []
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const team = customer ? customer.team.map((m) => (m.role === 'owner' ? { ...m, userId: v.ownerId } : m)) : [{ userId: v.ownerId, role: 'owner' as const }]
      const id = await save.mutateAsync({ id: customer?.id, input: { ...v, team } })
      toast.success(t(customer ? 'crm.customerUpdated' : 'crm.customerCreated'))
      onDone()
      if (!customer) navigate(`/customers/${id}`)
    } catch (e) {
      if (isApiError(e) && e.code === 'CUSTOMER_NAME_TAKEN') form.setError('name', { message: t('errors.CUSTOMER_NAME_TAKEN') })
      else toastError(e)
    }
  })
  const select = (name: 'projectId' | 'stage' | 'delivery' | 'ownerId', options: { value: string; label: string; description?: string }[]) => (
    <FormSelect id={`cu-${name}`} control={form.control} name={name} options={options} />
  )

  return (
    <Modal
      open
      size="xl"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Building2 />}
      title={t(customer ? 'crm.form.editTitle' : 'crm.form.createTitle')}
      description={t('crm.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('crm.form.sectionCompany')}>
          <Field id="cu-name" label={t('crm.fields.name')} error={errors.name?.message}>
            <Input id="cu-name" aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field id="cu-legal" label={t('crm.fields.legalName')}>
            <Input id="cu-legal" {...form.register('legalName')} />
          </Field>
          <Field id="cu-sector" label={t('crm.fields.sector')} error={errors.sector?.message}>
            <FormSelect
              id="cu-sector"
              control={form.control}
              name="sector"
              placeholder={t('crm.form.sectorPlaceholder')}
              options={sectors.map((sector) => ({ value: sector.id, label: sector.name, description: sector.code }))}
            />
          </Field>
          <Field id="cu-projectId" label={t('crm.fields.project')} error={errors.projectId?.message}>
            {select('projectId', projects.map((p) => ({ value: p.id, label: p.name })))}
          </Field>
          <Field id="cu-taxno" label={t('crm.fields.taxNo')} error={errors.taxNo?.message}>
            <Input id="cu-taxno" inputMode="numeric" maxLength={11} {...form.register('taxNo')} />
          </Field>
          <Field id="cu-taxoffice" label={t('crm.fields.taxOffice')}>
            <Input id="cu-taxoffice" {...form.register('taxOffice')} />
          </Field>
        </FormSection>
        <FormSection title={t('crm.form.sectionLinks')}>
          <Field id="cu-web" label={t('crm.links.website')} error={errors.website?.message} full>
            <Input id="cu-web" inputMode="url" placeholder="www.ornek.com" {...form.register('website')} />
          </Field>
          <Field id="cu-app" label={t('crm.links.app')} error={errors.appUrl?.message} hint={t('crm.links.appHint')}>
            <Input id="cu-app" inputMode="url" placeholder="https://musteri.orvapos.com" {...form.register('appUrl')} />
          </Field>
          <Field id="cu-admin" label={t('crm.links.admin')} error={errors.adminUrl?.message} hint={t('crm.links.adminHint')}>
            <Input id="cu-admin" inputMode="url" placeholder="https://admin.musteri.orvapos.com" {...form.register('adminUrl')} />
          </Field>
        </FormSection>
        <FormSection title={t('crm.form.sectionContact')}>
          <Field id="cu-contact" label={t('crm.fields.contact')} error={errors.contactName?.message}>
            <Input id="cu-contact" aria-invalid={!!errors.contactName} {...form.register('contactName')} />
          </Field>
          <Field id="cu-ctitle" label={t('crm.fields.contactTitle')}>
            <Input id="cu-ctitle" {...form.register('contactTitle')} />
          </Field>
          <Field id="cu-phone" label={t('crm.fields.phone')}>
            <Input id="cu-phone" inputMode="tel" placeholder="+90 5XX XXX XX XX" {...form.register('phone')} />
          </Field>
          <Field id="cu-email" label={t('crm.fields.email')} error={errors.email?.message}>
            <Input id="cu-email" type="email" {...form.register('email')} />
          </Field>
          <Field id="cu-city" label={t('crm.fields.city')}>
            <FormSelect
              id="cu-city"
              control={form.control}
              name="city"
              searchable
              loading={provincesQ.isLoading}
              placeholder={t('crm.form.selectCity')}
              searchPlaceholder={t('crm.form.searchCity')}
              options={withLegacy(provinces.map((p) => p.name), customer?.city)}
              onValueChange={() => form.setValue('district', '', { shouldDirty: true })}
            />
          </Field>
          <Field id="cu-district" label={t('crm.fields.district')}>
            <FormSelect
              id="cu-district"
              control={form.control}
              name="district"
              searchable
              disabled={!city}
              loading={districtsQ.isFetching}
              placeholder={t('crm.form.selectDistrict')}
              searchPlaceholder={t('crm.form.searchDistrict')}
              options={withLegacy((districtsQ.data ?? []).map((d) => d.name), city === customer?.city ? customer?.district : '')}
            />
          </Field>
          <Field id="cu-region" label={t('crm.fields.region')} hint={t('crm.form.regionHint')}>
            <FormSelect
              id="cu-region"
              control={form.control}
              name="region"
              placeholder={t('crm.form.selectRegion')}
              options={[{ value: '', label: t('crm.form.noRegion') }, ...withLegacy(regions.map((r) => r.name), customer?.region)]}
            />
          </Field>
          <Field id="cu-address" label={t('crm.fields.address')} full>
            <Input id="cu-address" {...form.register('address')} />
          </Field>
        </FormSection>
        <FormSection title={t('crm.form.sectionSales')}>
          <Field id="cu-stage" label={t('crm.fields.stage')}>
            {select('stage', STAGES.map((s) => ({ value: s, label: t(`crm.stage.${s}`) })))}
          </Field>
          <Field id="cu-delivery" label={t('crm.fields.delivery')}>
            {select('delivery', DELIVERY.map((s) => ({ value: s, label: t(`crm.delivery.${s}`) })))}
          </Field>
          <Field id="cu-ownerId" label={t('crm.fields.owner')} error={errors.ownerId?.message} full>
            {select('ownerId', staff.map((s) => ({ value: s.id, label: s.name, description: s.title })))}
          </Field>
        </FormSection>
      </div>
    </Modal>
  )
}
